import { BaseText, Meta, theme } from 'aimo-ui';
import ExternalPaymentInfoButton from 'components/account/paymentMethods/ExternalPaymentInfoButton';
import { showToast } from 'components/common/CustomToast';
import SelectableModalContentItem from 'components/common/SelectableModalContentItem';
import Icon from 'components/icons/Icon';
import AddPaymentMethod from 'components/payment/AddPaymentMethod';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useExternalPaymentMethods, {
  ExternalPaymentWrapper,
} from 'hooks/useExternalPaymentMethods';
import useSelectedCompanyBenefit from 'hooks/useSelectedCompanyBenefit';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import useShowPaymentCardStatus from 'hooks/useShowPaymentCardStatus';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native-gesture-handler';
import { parkingPaymentCardId } from 'states/common';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useUiStore } from 'stores/uiStore';
import styled from 'styled-components/native';
import { CenterColumn, CenterRow } from 'styles/ContainerStyles';
import { Tag, TagText } from 'styles/TextStyles';
import { PaymentDataProps } from 'types/PaymentData';
import {
  Card,
  CardStatus,
  CompanyBenefit,
  PaymentOption,
} from 'types/generatedSchemaTypes';
import {
  isMobilePayEnabledForCountry,
  showFeatureFlag,
} from 'utils/featureFlagUtils';
import {
  dedupePendingCards,
  enrichPaymentOptionsWithNames,
  getCardLogo,
  getExternalPaymentLogo,
  getWalletPaymentMethodIcon,
  getWalletPaymentMethodLabelKey,
  isCardSelectable,
  isPaymentOptionValidForVehicle,
  isWalletCard,
} from 'utils/paymentUtils';

const ErrorInfo = ({
  testID,
  expiresSoon,
}: {
  testID: string;
  expiresSoon: boolean;
}) => {
  return (
    <Icon
      name={`Info-Filled${expiresSoon ? '-Black' : ''}`}
      size="s"
      testID={`error-info-icon-${testID}`}
      color={expiresSoon ? theme.colors.boost.yellow : theme.colors.aimoUiRed}
    />
  );
};

const Row = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex: 1;
`;

const IconWrapper = styled.View<{ blocked: boolean }>`
  opacity: ${({ blocked }) => (blocked ? 0.5 : 1)};
`;

const paymentData: PaymentDataProps[] = [
  {
    title: 'payment.addPaymentCard',
    icon: 'plus',
    action: 'addCardModal',
    testID: 'add-credit-card-button',
  },
];

// Exported for tests (no separate module — the row is only used by this sheet).
export const PaymentItem = ({
  paymentCard,
  isSelected,
  companyBenefit,
  isBlocked = false,
  externalPayment,
  paymentOption,
  PaymentInfo,
}: {
  isSelected: boolean;
  paymentCard?: Card;
  testID?: string;
  companyBenefit?: CompanyBenefit;
  isBlocked?: boolean;
  externalPayment?: ExternalPaymentWrapper;
  paymentOption?: PaymentOption & { optionName?: string };
  PaymentInfo?: React.FC<{ item: ExternalPaymentWrapper }>;
}) => {
  const { t } = useTranslation();
  const {
    flags: { card_business },
  } = useContext(FeatureFlagsContext);
  const showBusinessCard = showFeatureFlag(card_business);

  const { setCurrentPaymentMethodId } = useParkingStore((state) => state);
  const {
    cardId,
    status,
    category,
    type,
    description,
    lastNums,
    expMonth,
    expYear,
  } = paymentCard || {};

  const { uid, name: benefitName } = companyBenefit ?? {};
  const {
    externalPaymentType: externalPaymentMethod,
    name: externalPaymentName,
  } = externalPayment ?? {};
  const isBusiness = category === 'business';
  const { isErrorStatus, expiresSoon } = useShowPaymentCardStatus(
    (status || 'Active') as CardStatus,
    expMonth || '',
    expYear || ''
  );

  const paymentMethodUid =
    paymentCard?.cardId ??
    uid ??
    externalPayment?.externalPaymentType ??
    paymentOption?.optionId;

  const name = benefitName ?? externalPaymentName ?? paymentOption?.optionName;

  const getRegistrationPlateText = () => {
    if (
      !paymentOption?.registrationPlates ||
      paymentOption.registrationPlates.length === 0
    ) {
      return t('paymentMethods.anyVehicle');
    }

    if (paymentOption.registrationPlates.length === 1) {
      return paymentOption.registrationPlates[0]?.plateText ?? '';
    }

    return t('paymentMethods.vehicleCount', {
      count: paymentOption.registrationPlates.length,
    });
  };

  const handleSetPayment = () => {
    if (isErrorStatus && paymentCard) {
      showToast(
        t(`error.paymentCardStatuses.${status?.toLowerCase()}`, 'error')
      );
    } else {
      // For parking we now use zustand store to manage the state,
      // We need to still use both zustand and reactive var for now until we have moved everything to zustand
      setCurrentPaymentMethodId(paymentMethodUid || undefined);
      parkingPaymentCardId(paymentMethodUid || undefined);
      useUiStore.getState().setSheetModal('');
    }
  };

  // Stored wallet card (Apple Pay/Google Pay): custom name first, wallet label
  // fallback; the wallet brand mark, never the underlying scheme logo; and no
  // PAN row (the wallet card's PAN is not meaningful). Mirrors CardItem /
  // getPaymentMethodInfo.
  const walletCard = isWalletCard(paymentCard);
  const walletLabelKey = getWalletPaymentMethodLabelKey(
    paymentCard?.paymentMethod
  );
  const walletFallbackTitle = walletLabelKey ? t(walletLabelKey) : type;
  const cardTitle = description || (walletCard ? walletFallbackTitle : type);

  const modalContentDetails = () => {
    if (paymentCard) {
      return (
        <>
          <CenterColumn>
            <BaseText testID={`credit-card-type-${cardId}`}>
              {cardTitle}
            </BaseText>
            {!walletCard && (
              <Meta
                color={theme.colors.gray300}
                testID={`payment-card-trailing-numbers-${cardId}`}>
                **** {lastNums}
              </Meta>
            )}
          </CenterColumn>
          <CenterRow>
            {isBusiness && showBusinessCard && (
              <Tag testID={`business-tag-${cardId}`}>
                <TagText>{t('paymentMethods.business')}</TagText>
              </Tag>
            )}
          </CenterRow>
        </>
      );
    }

    if (paymentOption) {
      if (paymentOption.method === 'MOBILEPAY') {
        return (
          <CenterColumn>
            <BaseText testID={`mobilepay-wallet-${paymentOption.optionId}`}>
              {t('paymentMethods.mobilePay')}
            </BaseText>
          </CenterColumn>
        );
      }
      return (
        <CenterColumn>
          <BaseText testID={`payment-option-name-${paymentOption.optionId}`}>
            {paymentOption.optionName}
          </BaseText>
          <Meta
            color={theme.colors.gray300}
            testID={`payment-option-plates-${paymentOption.optionId}`}>
            {getRegistrationPlateText()}
          </Meta>
        </CenterColumn>
      );
    }

    return (
      <BaseText
        color={isBlocked ? theme.colors.gray300 : undefined}
        testID={`company-permit-name-${name}`}>
        {name}
      </BaseText>
    );
  };

  const paymentItemTitle = () => {
    if (!paymentCard) {
      return name ?? '';
    }
    return walletCard ? (cardTitle ?? '') : `${cardTitle} **** ${lastNums}`;
  };

  const getIconName = () => {
    if (paymentOption?.method === 'MOBILEPAY') {
      return 'Mobile-Pay';
    }
    if (paymentCard) {
      return walletCard
        ? getWalletPaymentMethodIcon(paymentCard.paymentMethod)
        : getCardLogo(type);
    }
    if (paymentOption) {
      return 'Business-Filled';
    }
    return getExternalPaymentLogo(externalPaymentMethod);
  };

  return (
    <SelectableModalContentItem
      isSelected={isSelected}
      disabled={isBlocked}
      onPress={() => handleSetPayment()}
      testID={`credit-card-wrap-${paymentMethodUid}`}
      itemTitle={paymentItemTitle()}
      iconNode={
        <IconWrapper blocked={isBlocked}>
          <Icon
            testID={`payment-method-icon-${paymentMethodUid}`}
            name={getIconName()}
            color={paymentCard ? undefined : theme.colors.boost.green}
            size="xl"
          />
        </IconWrapper>
      }
      iconNodeRight={
        <>
          {(isErrorStatus || expiresSoon) && (
            <ErrorInfo
              expiresSoon={expiresSoon}
              testID={`${paymentMethodUid}`}
            />
          )}
          {PaymentInfo && externalPayment && (
            <PaymentInfo item={externalPayment} />
          )}
        </>
      }>
      <Row>{modalContentDetails()}</Row>
    </SelectableModalContentItem>
  );
};

const NON_RECURRING_PAYMENT_TYPES = ['SWISH'];

const SelectPaymentMethodModalContent = ({
  recurringOnly,
}: {
  recurringOnly?: boolean;
}) => {
  const {
    customer: {
      paymentCards,
      companyBenefits,
      paymentOptions,
      countryCode: customerCountryCode,
      contactEmail,
    },
  } = useContext(AccountContext);
  const {
    flags: {
      company_benefits,
      ca_with_license_plates,
      enable_mobilepay_by_country,
    },
  } = useContext(FeatureFlagsContext);
  const zoneCountryCode = useParkzoneStore((state) => state.parkZoneCountry);
  const showMobilePay = isMobilePayEnabledForCountry(
    enable_mobilepay_by_country,
    zoneCountryCode,
    contactEmail
  );
  const showPaymentOptions = showFeatureFlag(ca_with_license_plates);
  const showCompanyPermits =
    showFeatureFlag(company_benefits) && !showPaymentOptions;
  const selectedPaymentMethod = useSelectedPaymentMethod();
  const { companyBenefit, isUseAllowed } = useSelectedCompanyBenefit();
  const selectedVehicle = useSelectedParkingVehicle();
  const {
    externalPaymentMethods,
    isUseAllowed: isExternalPaymentUseAllowed,
    externalPaymentMethod,
  } = useExternalPaymentMethods();

  const showMobilePayOptions = showMobilePay && customerCountryCode === 'FI';
  // Filter and enrich corporate account payment options using utility function
  const corporateAccountOptions = useMemo(() => {
    // Recurring (Aimo+) are charged to a card only
    if (!showPaymentOptions || recurringOnly) {
      return [];
    }

    const corporateOptions = (paymentOptions || []).filter(
      (option) => option.method === 'CORPORATE_ACCOUNT'
    );
    return enrichPaymentOptionsWithNames(corporateOptions, companyBenefits);
  }, [paymentOptions, companyBenefits, showPaymentOptions, recurringOnly]);

  const mobilePayOptions = (paymentOptions || []).filter(
    (option) => option.method === 'MOBILEPAY' && showMobilePayOptions
  );

  // Collapse stale pending (non-Active) rows — abandoned/retried Adyen card-add
  // flows mint an `Adding` card each, so without this the list would show one
  // in-progress row per attempt.
  const selectableCards = useMemo(
    () => dedupePendingCards(paymentCards ?? []),
    [paymentCards]
  );
  return (
    <ScrollView>
      {mobilePayOptions.map((option) => (
        <PaymentItem
          isSelected={selectedPaymentMethod?.optionId === option.optionId}
          key={`${option?.optionId}`}
          paymentOption={option}
        />
      ))}
      {selectableCards.map((paymentCard, index) => {
        // Only Adyen gates on Active: a pending (Adding/Verifying) Adyen card
        // can't be charged until the webhook flips it Active, so block it.
        // Nets cards keep their prior always-selectable behaviour (matches
        // the provider-aware predicate in useSelectedPaymentMethod).
        const isAdyenCard =
          paymentCard.paymentProvider === 'ADYEN_SE' ||
          paymentCard.paymentProvider === 'ADYEN_FI';
        return (
          <PaymentItem
            isSelected={selectedPaymentMethod?.cardId === paymentCard.cardId}
            isBlocked={isAdyenCard && !isCardSelectable(paymentCard.status)}
            key={`${paymentCard?.cardId}-${index}`}
            paymentCard={paymentCard}
          />
        );
      })}
      {showCompanyPermits &&
        companyBenefits?.map((benefit, i) => (
          <PaymentItem
            isSelected={
              companyBenefit?.uid === benefit?.uid &&
              benefit.countryCode === zoneCountryCode
            }
            key={`${benefit?.uid}-${i}`}
            companyBenefit={benefit ?? undefined}
            isBlocked={isUseAllowed || benefit?.countryCode !== zoneCountryCode}
          />
        ))}
      {showPaymentOptions &&
        corporateAccountOptions?.map((option, index) => (
          <PaymentItem
            isSelected={selectedPaymentMethod?.optionId === option.optionId}
            key={`payment-option-${option.optionId}-${index}`}
            paymentOption={option}
            isBlocked={
              isUseAllowed ||
              !(
                zoneCountryCode &&
                option.includedCountries?.includes(zoneCountryCode)
              ) ||
              !isPaymentOptionValidForVehicle(option, selectedVehicle)
            }
          />
        ))}
      {externalPaymentMethods
        ?.filter(
          (external) =>
            !recurringOnly ||
            !NON_RECURRING_PAYMENT_TYPES.includes(external.externalPaymentType)
        )
        .map((external) => (
          <PaymentItem
            isSelected={
              external.externalPaymentType ===
              externalPaymentMethod?.externalPaymentType
            }
            key={`external-payment-${external}`}
            isBlocked={isExternalPaymentUseAllowed}
            externalPayment={external}
            PaymentInfo={ExternalPaymentInfoButton}
          />
        ))}
      <AddPaymentMethod paymentData={paymentData} />
    </ScrollView>
  );
};

export default SelectPaymentMethodModalContent;
